/**
 * The order of the draft rows: what needs the sales person first, then what is settled,
 * then what is not quoted. Pure functions over the lines, shared by the table (laptop),
 * the cards (phone) and the pane (Outlook), so the three surfaces read the same.
 *
 * Within a group the rows keep the order of the enquiry: a sales person reads the mail
 * top to bottom and finds the lines in the same order.
 */

import { lineWordOf } from "@/features/case-review/case-action";
import { lineOutcome } from "@/features/case-review/line-semantics";
import { type QuoteLine } from "@/lib/mock";
import {
  DRAFT_GROUPS,
  draftCountText,
  type DraftGroup,
  LINE_STATUS_WORDS,
  type LineStatusWord,
} from "@/lib/sales-flow";

/** One row of the draft: the line, its word and its group. */
export interface DraftRow {
  line: QuoteLine;
  word: LineStatusWord;
  group: DraftGroup;
  /** Position of the line in the stored draft (what the API expects); -1 when unknown. */
  lineIndex: number;
  /** The row's number in the enquiry, for the labels ("line 2"). */
  position: number;
}

/** The group of a word: pick and confirm need the person, matched is settled, no item is folded. */
export function groupOf(word: LineStatusWord): DraftGroup {
  switch (word) {
    case LINE_STATUS_WORDS.pick:
    case LINE_STATUS_WORDS.confirm:
      return DRAFT_GROUPS.needsYou;
    case LINE_STATUS_WORDS.matched:
      return DRAFT_GROUPS.matched;
    case LINE_STATUS_WORDS.noItem:
      return DRAFT_GROUPS.nothingToQuote;
  }
}

/** The sort rank of a row: pick, confirm, matched by the engine, decided by a person, no item. */
function rankOf(line: QuoteLine, word: LineStatusWord): number {
  switch (word) {
    case LINE_STATUS_WORDS.pick:
      return 0;
    case LINE_STATUS_WORDS.confirm:
      return 1;
    case LINE_STATUS_WORDS.matched:
      // A line the engine matched comes before one a person decided: the first is what
      // I2Q claims, the second what a colleague already settled.
      return lineOutcome(line).kind === "decided" ? 3 : 2;
    case LINE_STATUS_WORDS.noItem:
      return 4;
  }
}

/**
 * The rows of the draft in triage order: pick, confirm, matched, decided, no item, each
 * in enquiry order. Hidden lines (removed requests, stand-ins) are left out, as in
 * `wordedLines`. `allLines` is the stored draft, for the line index the API expects.
 */
export function groupedRows(
  lines: readonly QuoteLine[],
  allLines: readonly QuoteLine[] = lines,
): DraftRow[] {
  const rows: (DraftRow & { rank: number })[] = [];
  lines.forEach((line, index) => {
    const word = lineWordOf(line);
    if (!word) {
      return;
    }
    rows.push({
      line,
      word,
      group: groupOf(word),
      lineIndex: allLines.findIndex((entry) => entry.id === line.id),
      position: index + 1,
      rank: rankOf(line, word),
    });
  });
  // A stable sort: equal ranks keep the enquiry order.
  rows.sort((a, b) => a.rank - b.rank || a.position - b.position);
  return rows.map(({ rank: _rank, ...row }) => row);
}

/**
 * The row the keys go to after `lineId` was decided: the next row of "Needs you" under
 * it in the order of the page, else the first one over it; null when no other row needs
 * the person. A row a person decided already is not a target.
 */
export function nextRowThatNeedsYou(rows: readonly DraftRow[], lineId: string): DraftRow | null {
  const at = rows.findIndex((row) => row.line.id === lineId);
  const waits = (row: DraftRow) =>
    row.group === DRAFT_GROUPS.needsYou && !row.line.decision && row.line.id !== lineId;
  return rows.find((row, index) => index > at && waits(row)) ?? rows.find(waits) ?? null;
}

export interface StripCounts {
  pick: number;
  confirm: number;
  matched: number;
  noItem: number;
}

/** How many rows need a pick, a confirmation, are matched, or have no item. */
export function stripCounts(lines: readonly QuoteLine[]): StripCounts {
  const counts: StripCounts = { pick: 0, confirm: 0, matched: 0, noItem: 0 };
  for (const line of lines) {
    switch (lineWordOf(line)) {
      case LINE_STATUS_WORDS.pick:
        counts.pick += 1;
        break;
      case LINE_STATUS_WORDS.confirm:
        counts.confirm += 1;
        break;
      case LINE_STATUS_WORDS.matched:
        counts.matched += 1;
        break;
      case LINE_STATUS_WORDS.noItem:
        counts.noItem += 1;
        break;
      default:
        break;
    }
  }
  return counts;
}

/** The count badges of the strip, in group order, zero counts left out. */
export function stripBadges(
  counts: StripCounts,
): { kind: keyof StripCounts; word: LineStatusWord; text: string }[] {
  const order: { kind: keyof StripCounts; word: LineStatusWord }[] = [
    { kind: "pick", word: LINE_STATUS_WORDS.pick },
    { kind: "confirm", word: LINE_STATUS_WORDS.confirm },
    { kind: "matched", word: LINE_STATUS_WORDS.matched },
    { kind: "noItem", word: LINE_STATUS_WORDS.noItem },
  ];
  return order
    .filter(({ kind }) => counts[kind] > 0)
    .map(({ kind, word }) => ({ kind, word, text: draftCountText(kind, counts[kind]) }));
}
